/**
 * Learn tracks: authored, ordered curricula ("Master the Scanner") composed of
 * lessons. A lesson references a walkthrough by its KB `slug` (launched by id,
 * not fuzzy-retrieved) plus Learn-facing copy. This is a manifest ABOVE the
 * walkthrough catalog; see
 * docs/superpowers/specs/2026-08-06-companion-learn-page-design.md.
 *
 * Progress (which lessons are done, the active track) lives in
 * learn-progress-store; this file is pure authored content.
 */

export interface Lesson {
  /** KB walkthrough slug: the id the launcher will run (increment 2). */
  slug: string
  /** The panel this lesson's walkthrough runs in (opened first if not present). */
  panel: string
  title: string
  blurb: string
}

export interface Track {
  id: string
  title: string
  blurb: string
  lessons: Lesson[]
}

export const TRACKS: Track[] = [
  {
    id: 'master-scanner',
    title: 'Master the Scanner',
    blurb: 'Go from an empty scan to a tuned, color-coded, saved setup.',
    lessons: [
      {
        slug: 'scanner.edit-filters',
        panel: 'scanner',
        title: 'Filter the scan',
        blurb: 'Narrow the scanner to just the stocks you care about.',
      },
      {
        slug: 'scanner.edit-columns',
        panel: 'scanner',
        title: 'Add columns',
        blurb: 'Show the data columns that matter for your setup.',
      },
      {
        slug: 'columns-dashboard.color-code-column',
        panel: 'scanner',
        title: 'Color-code a column',
        blurb: 'Highlight values by rule so opportunities jump out.',
      },
      {
        slug: 'scanner.advanced-rvol',
        panel: 'scanner',
        title: 'Customize RVOL',
        blurb: 'Tune the relative-volume baseline to your strategy.',
      },
      {
        slug: 'scanner.exclude-ticker',
        panel: 'scanner',
        title: 'Exclude a ticker',
        blurb: 'Hide symbols you never want to see in the scan.',
      },
    ],
  },
]

/** Look up a track by id (undefined if unknown). */
export function trackById(id: string): Track | undefined {
  return TRACKS.find((t) => t.id === id)
}

/** All lesson slugs across all tracks (for progress bookkeeping). */
export function allLessonSlugs(): string[] {
  return TRACKS.flatMap((t) => t.lessons.map((l) => l.slug))
}
